import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { UserInviteMetaResponse } from '../../../core/auth/auth.models';
import { TenantBrandingService } from '../../../core/branding/tenant-branding.service';

@Component({
  selector: 'app-staff-invite-accept-page',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, TranslatePipe, DatePipe],
  template: `
    <div class="invite-page" [style.--tenant-primary]="tenantPrimaryColor" [style.--tenant-secondary]="tenantSecondaryColor" [style.--tenant-accent]="tenantAccentColor">
      <div class="invite-page__card">
        @if (brandingLogoUrl) {
          <img [src]="brandingLogoUrl" alt="Tenant logo" class="invite-page__logo" />
        }
        <h1 class="invite-page__title">{{ tenantDisplayName || ('auth.inviteAcceptTitle' | t) }}</h1>

        @if (isMetaLoading) {
          <p class="invite-page__meta">{{ 'auth.inviteAcceptLoading' | t }}</p>
        } @else if (successText) {
          <p class="invite-page__success">{{ successText | t }}</p>
          <a [routerLink]="['/', tenantSlug, 'login']">{{ 'auth.backToLogin' | t }}</a>
        } @else if (errorText && !meta) {
          <p class="invite-page__error">{{ errorText | t }}</p>
          <a [routerLink]="['/', tenantSlug, 'login']">{{ 'auth.backToLogin' | t }}</a>
        } @else if (meta) {
          <p class="invite-page__subtitle">{{ 'auth.staffInviteAcceptSubtitle' | t }}</p>

          <div class="invite-page__summary">
            <div><strong>{{ 'Role' | t }}:</strong> {{ roleLabelKey | t }}</div>
            <div><strong>{{ 'auth.email' | t }}:</strong> {{ meta.emailMasked }}</div>
            <div>
              <strong>{{ 'auth.inviteExpiresAt' | t }}:</strong>
              {{ meta.expiresAt | date:'dd.MM.yyyy HH:mm' }}
            </div>
          </div>

          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="invite-page__form">
            <div class="invite-page__field">
              <label for="password">{{ 'auth.password' | t }}</label>
              <input
                id="password"
                type="password"
                formControlName="password"
                [placeholder]="'auth.enterPassword' | t"
              />
            </div>

            <div class="invite-page__field">
              <label for="confirmPassword">{{ 'auth.confirmPassword' | t }}</label>
              <input
                id="confirmPassword"
                type="password"
                formControlName="confirmPassword"
                [placeholder]="'auth.confirmPassword' | t"
              />
            </div>

            @if (errorText) {
              <div class="invite-page__error">{{ errorText | t }}</div>
            }

            <button type="submit" [disabled]="isSubmitting">
              {{ (isSubmitting ? 'auth.inviteAcceptSubmitting' : 'auth.inviteAcceptSubmit') | t }}
            </button>
          </form>
        }
      </div>
    </div>
  `,
  styles: [`
    .invite-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--tenant-primary, #2563eb) 18%, transparent), transparent 34%),
        linear-gradient(160deg, color-mix(in srgb, var(--tenant-secondary, #0f172a) 7%, white), #f8fafc 55%);
      padding: 24px;
      box-sizing: border-box;
    }

    .invite-page__card {
      width: 100%;
      max-width: 460px;
      background: #ffffff;
      border-radius: 16px;
      padding: 28px;
      box-sizing: border-box;
      box-shadow: 0 18px 40px color-mix(in srgb, var(--tenant-secondary, #0f172a) 16%, transparent);
      display: grid;
      gap: 14px;
    }

    .invite-page__logo {
      width: auto;
      max-width: 180px;
      max-height: 52px;
      display: block;
      margin-bottom: 2px;
      object-fit: contain;
    }

    .invite-page__title {
      margin: 0;
      font-size: 24px;
      color: var(--tenant-secondary, #111827);
    }

    .invite-page__subtitle,
    .invite-page__meta {
      margin: 0;
      color: #374151;
    }

    .invite-page__summary {
      display: grid;
      gap: 8px;
      padding: 12px 14px;
      border-radius: 10px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      color: #111827;
      font-size: 14px;
    }

    .invite-page__form {
      display: grid;
      gap: 14px;
    }

    .invite-page__field {
      display: grid;
      gap: 8px;
    }

    .invite-page__field label {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }

    .invite-page__field input {
      height: 42px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      outline: none;
    }

    .invite-page__field input:focus {
      border-color: var(--tenant-primary, #2563eb);
    }

    .invite-page__error {
      color: #b91c1c;
      font-size: 14px;
    }

    .invite-page__success {
      margin: 0;
      color: #166534;
      font-size: 14px;
    }

    .invite-page__form button {
      height: 42px;
      border: none;
      border-radius: 8px;
      background: var(--tenant-primary, #2563eb);
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .invite-page__form button:disabled {
      opacity: 0.7;
      cursor: default;
    }
  `],
})
export class StaffInviteAcceptPageComponent {
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly tenantBranding = inject(TenantBrandingService);

  protected readonly tenantSlug = this.route.snapshot.paramMap.get('companySlug')?.trim().toLowerCase() ?? '';
  protected readonly token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';

  protected meta: UserInviteMetaResponse | null = null;
  protected isMetaLoading = true;
  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected successText: string | null = null;
  protected tenantDisplayName = '';
  protected brandingLogoUrl: string | null = null;
  protected tenantPrimaryColor = '#2563eb';
  protected tenantSecondaryColor = '#0f172a';
  protected tenantAccentColor = '#f59e0b';

  protected readonly form = this.formBuilder.nonNullable.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]],
  });

  protected get roleLabelKey(): string {
    const role = this.meta?.role?.trim().toLowerCase();
    return role ? `role.${role}` : 'common.noData';
  }

  constructor() {
    if (this.tenantSlug) {
      this.tenantBranding.getBranding(this.tenantSlug).subscribe((branding) => {
        const vars = this.tenantBranding.toCssVars(branding);
        this.tenantDisplayName = branding?.displayName ?? '';
        this.brandingLogoUrl = branding?.logoUrl ?? null;
        this.tenantPrimaryColor = vars['--tenant-primary'];
        this.tenantSecondaryColor = vars['--tenant-secondary'];
        this.tenantAccentColor = vars['--tenant-accent'];
      });
    }

    if (!this.tenantSlug || !this.token) {
      this.isMetaLoading = false;
      this.errorText = 'auth.inviteInvalid';
      return;
    }

    this.authService
      .getUserInviteMeta(this.token)
      .pipe(finalize(() => (this.isMetaLoading = false)))
      .subscribe({
        next: (meta) => {
          if (meta.companySlug.trim().toLowerCase() !== this.tenantSlug) {
            this.errorText = 'auth.inviteInvalid';
            return;
          }

          this.meta = meta;
        },
        error: (error: unknown) => {
          this.errorText = this.resolveMetaErrorKey(error);
        },
      });
  }

  protected onSubmit(): void {
    if (!this.meta) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    if (value.password !== value.confirmPassword) {
      this.errorText = 'auth.passwordMismatch';
      return;
    }

    this.errorText = null;
    this.isSubmitting = true;

    this.authService
      .acceptUserInvite({
        token: this.token,
        password: value.password,
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.successText = 'auth.inviteAcceptSuccess';
        },
        error: (error: unknown) => {
          this.errorText = this.resolveAcceptErrorKey(error);
        },
      });
  }

  private resolveMetaErrorKey(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'auth.inviteMetaFailed';
    }

    if (error.status === 404) {
      return 'auth.inviteInvalid';
    }

    if (error.status === 410) {
      return 'auth.inviteExpired';
    }

    return 'auth.inviteMetaFailed';
  }

  private resolveAcceptErrorKey(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'auth.inviteAcceptFailed';
    }

    if (error.status === 404) {
      return 'auth.inviteInvalid';
    }

    if (error.status === 410) {
      return 'auth.inviteExpired';
    }

    if (error.status === 409) {
      return 'auth.inviteConflict';
    }

    return 'auth.inviteAcceptFailed';
  }
}
